import axios, { AxiosInstance } from 'axios'
import { StatusCodeEnum } from "../shared/enums"
import { KeycloakService } from './KeycloakService'
import { COOKIE_TOKEN_KEY } from '../shared/constants'
import { CookieUtils } from '../shared/utils/CookieUtils'

export class Api {
    private api: AxiosInstance | undefined
    private static instance: Api | null = null

    private constructor() { }

    public static async getInstance(): Promise<Api> {
        if (!Api.instance) {
            Api.instance = new Api()
            await Api.instance.init()
        }
        return Api.instance
    }

    private async init() {
        this.api = axios.create();

        this.api.interceptors.response.use(
            (response) => response,
            async (error) => {
                if (error?.response?.status === StatusCodeEnum.UNAUTHORIZED) {
                    // Tentar obter um novo token se receber 401
                    try {
                        const tokenFromCookie = CookieUtils.getCookie(COOKIE_TOKEN_KEY);

                        if (tokenFromCookie) {
                            this.addAuthorization(tokenFromCookie);
                            // Repetir a requisição original com o novo token
                            const originalRequest = error.config;
                            return this.api?.request(originalRequest);
                        }

                        const token = await KeycloakService.getToken();
                        this.addAuthorization(token.access_token);
                        CookieUtils.setCookie(COOKIE_TOKEN_KEY, token.access_token);

                        // Repetir a requisição original com o novo token
                        const originalRequest = error.config;
                        return this.api?.request(originalRequest);
                    } catch (refreshError) {
                        console.error("Erro ao renovar o token:", refreshError);
                        throw new Error('Usuário não autorizado.');
                    }
                }

                if (error?.code === StatusCodeEnum.ERR_NETWORK) {
                    throw new Error('Erro de rede. Verifique sua conexão.');
                }

                return Promise.reject(error);
            },
        );

        try {
            // Obter token inicial
            const token = await KeycloakService.getToken();
            if (token) {
                this.addAuthorization(token.access_token);
                CookieUtils.setCookie(COOKIE_TOKEN_KEY, token.access_token);
            } else {
                console.error("Token não obtido");
            }
        } catch (error) {
            console.error("Erro ao inicializar a API com token:", error);
            // Continuar sem token, o interceptor tentará novamente quando necessário
        }
    }

    public static resetInstance() {
        Api.instance = null
    }

    public getApi(): AxiosInstance {
        if (!this.api) {
            throw new Error('API not initialized')
        }
        return this.api
    }

    public addAuthorization(token: string) {
        if (this.api) {
            this.api.defaults.headers.common.Authorization = `Bearer ${token}`;
        }
    }

    public removeAuthorization() {
        if (this.api) {
            delete this.api.defaults.headers.common.Authorization;
        }
    }
}

export default Api